import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import BlackTriangle from '@/assets/icons/cover-images/black-triangle.png';
import BlackWatermark from '@/assets/icons/cover-images/demilked_black.png';
import WhiteWatermark from '@/assets/icons/cover-images/demilked_white.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadSelectedImagesAsZip,
  downloadImagesAsZip as downloadZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var13');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            const fullImage = new Image();
            fullImage.src = variation.imageSrc;
            fullImage.classList.add('full-image-var13');
            imageHolder.appendChild(fullImage);

            const bottomText = document.createElement('div');

            if (variation.circleImageSrc) {
              const circleImage = new Image();
              circleImage.src = variation.circleImageSrc;
              circleImage.classList.add('circle-image-var13');

              if (variation.imagePosition === 'left') {
                circleImage.classList.add('position-left');
              } else if (variation.imagePosition === 'custom') {
                circleImage.classList.add('position-custom');
                circleImage.style.right = variation.imageLeft
                  ? `${variation.imageLeft}px`
                  : '40px';
                circleImage.style.top = variation.imageTop
                  ? `${variation.imageTop}px`
                  : '-536px';
              }

              bottomText.appendChild(circleImage);
            }

            if (variation.text) {
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var13');
              let text = variation.text;
              if (variation.textBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(`${text}`, {
                  maxWidth: 800,
                  fontFamily: 'SourceSansProBold',
                  minFontSize:
                    variation.textFontSize !== 0 ? variation.textFontSize : 60,
                });
                bottomTextP.innerHTML = text;
              }

              const triangle = new Image();
              triangle.classList.add('triangle-icon-var13');
              triangle.src = BlackTriangle;
              bottomTextP.appendChild(triangle);

              bottomText.appendChild(bottomTextP);
            }

            imageHolder.appendChild(bottomText);

            if (variation.textFontSize) {
              bottomText.style.fontSize = `${variation.textFontSize}px`;
            }

            const logoWatermark = new Image();
            logoWatermark.classList.add('logo-watermark-var13');
            if (variation.logoWatermark === 'black') {
              logoWatermark.src = BlackWatermark;
            } else {
              logoWatermark.src = WhiteWatermark;
            }
            imageHolder.appendChild(logoWatermark);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var13',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
